import React, { useState } from 'react';
import { UnscheduledEvent } from '../../types';
import {
  FleetRecord,
  getTailNumbers,
  getOperatorByTail,
  getAircraftTypeByTail,
} from '../../services/fleetService';
import { MOVE_EVENT_OPTION, RECORD_TYPES, ONGOING_DELAY_LABEL } from '../../constants';

/**
 * Props for {@link AircraftGeneralSection}.
 */
interface AircraftGeneralSectionProps {
  formData: UnscheduledEvent;
  mode: 'view' | 'edit' | 'create';
  isReadOnly: boolean;
  isScheduled: boolean;
  isDeferred: boolean;
  fleetData: FleetRecord[];
  updateField: (field: keyof UnscheduledEvent, value: string | number | boolean) => void;
  /** Fills the flight fields for a tail; `iropSelected` applies the RTB/ATO/ATB mapping. */
  populateFlights: (tail: string, iropSelected: boolean) => void;
  /** Highlights Tail Number as required when empty (all record types). */
  tailRequired?: boolean;
  /** Highlights Station as required when empty (Scheduled records). */
  stationRequired?: boolean;
  /** When creating, disables Tail Number and Status until Time Declared is entered. */
  awaitingTimeDeclared?: boolean;
  /** Marks a Triage-populated status as requiring user verification. */
  verifyStatus?: boolean;
  /** Marks APES flight fields triggered by a Triage CREATE for verification. */
  verifyFlightFields?: boolean;
  /**
   * Whether the record is eligible to be moved (an active record open in edit
   * mode). Drives whether the Status dropdown offers the "Move Event" action.
   */
  canMove?: boolean;
  /** The current record type, so the move-target picker excludes it. */
  currentRecordType?: string;
  /** The destination record type chosen for a move (empty until picked). */
  moveTargetType?: string;
  /** Sets the destination record type for a move. */
  onMoveTargetChange?: (targetType: string) => void;
}

/**
 * Renders the Aircraft Info and General field rows of the event modal:
 * tail number (with fleet auto-population), carrier, previous/next flight,
 * status, station, delay minutes, and DEA package impact.
 */
const AircraftGeneralSection: React.FC<AircraftGeneralSectionProps> = ({
  formData,
  mode,
  isReadOnly,
  isScheduled,
  isDeferred,
  fleetData,
  updateField,
  populateFlights,
  tailRequired,
  stationRequired,
  awaitingTimeDeclared,
  verifyStatus,
  verifyFlightFields,
  canMove,
  currentRecordType,
  moveTargetType,
  onMoveTargetChange,
}) => {
  const isMoveMode = formData.status === MOVE_EVENT_OPTION;
  // Whether the Delay (Minutes) field currently has focus. While focused (and
  // editable), the ONGO placeholder is cleared so the user can type the actual
  // minutes; on blur with no duration entered it reappears.
  const [isDelayFieldFocused, setIsDelayFieldFocused] = useState<boolean>(false);
  const statusNeedsVerification: boolean = Boolean(verifyStatus && formData.status);
  const needsFlightVerification = (value: string | undefined): boolean =>
    Boolean(verifyFlightFields && value);
  const moveTargetOptions = RECORD_TYPES.filter((type) => type !== currentRecordType);

  const handleTailChange = (tail: string): void => {
    updateField('tailNumber', tail);
    if (isScheduled) return;
    // Seed carrier/aircraft type from the fleet list for instant feedback; the
    // APES carrier and flight fields are filled by populateFlights (which also
    // applies the RTB/ATO/ATB mapping when an IROP is selected).
    const operator = getOperatorByTail(fleetData, tail);
    if (operator) updateField('carrier', operator);
    const aircraftType = getAircraftTypeByTail(fleetData, tail);
    if (aircraftType) {
      updateField('aircraftType' as keyof UnscheduledEvent, aircraftType);
    }
    const iropSelected = !!(
      formData.returnToBlock ||
      formData.abortedTakeOff ||
      formData.airTurnBack ||
      formData.diversion
    );
    populateFlights(tail, iropSelected);
  };

  const handleDelayChange = (value: string): void => {
    const digits = value.replace(/[^0-9]/g, '');
    const val = digits ? parseInt(digits, 10) : 0;
    updateField('delayTimeMinutes', val);
    if (val > 0) updateField('delay', true);
  };

  const delayMinutes: number = Number(formData.delayTimeMinutes);
  // Show the actual minutes once entered; otherwise show the ONGO placeholder
  // while the Delay IROP is checked and the field is not being edited.
  const showOngoingDelay: boolean =
    !(delayMinutes > 0) && !!formData.delay && !(isDelayFieldFocused && !isReadOnly);
  const delayFieldValue: string =
    delayMinutes > 0 ? String(delayMinutes) : showOngoingDelay ? ONGOING_DELAY_LABEL : '';

  const handleDeaChange = (value: string): void => {
    const digits = value.replace(/[^0-9]/g, '');
    updateField('deaImpactedPackageCount', digits ? parseInt(digits, 10) : 0);
  };

  return (
    <>
      {/* Aircraft Info */}
      <div className="field-row">
        <label className="field-label">Aircraft Info</label>
        <div className="field-group">
          <div className="field-input-block">
            <span className="field-input-label">Tail Number</span>
            {isReadOnly ? (
              <input type="text" value={formData.tailNumber} readOnly className="field-input" />
            ) : (
              <>
                <input
                  type="text"
                  list="tail-number-options"
                  value={formData.tailNumber}
                  onChange={(e) => handleTailChange(e.target.value.toUpperCase())}
                  disabled={awaitingTimeDeclared}
                  className={`field-input${tailRequired && !formData.tailNumber && !awaitingTimeDeclared ? ' field-required-missing' : ''}`}
                  placeholder="Type or select tail..."
                  autoComplete="off"
                />
                <datalist id="tail-number-options">
                  {getTailNumbers(fleetData).map((tail) => (
                    <option key={tail} value={tail} />
                  ))}
                </datalist>
              </>
            )}
          </div>
          {!isScheduled && (
            <div className="field-input-block">
              <span className="field-input-label">Carrier</span>
              <input
                type="text"
                value={formData.carrier}
                readOnly
                className="field-input"
                placeholder="Auto-populated"
              />
            </div>
          )}
          {isScheduled && (
            <div className="field-input-block">
              <span className="field-input-label">Carrier</span>
              <input
                type="text"
                value={formData.carrier}
                readOnly={isReadOnly}
                onChange={(e) => updateField('carrier', e.target.value)}
                className="field-input"
                placeholder="Enter carrier"
              />
            </div>
          )}
          {!isScheduled && !isDeferred && (
            <div className="field-input-block">
              <span className="field-input-label">Previous Flight</span>
              <input
                type="text"
                value={formData.inboundFlightNumber}
                readOnly={isReadOnly}
                onChange={(e) => updateField('inboundFlightNumber', e.target.value.toUpperCase())}
                className={`field-input${needsFlightVerification(formData.inboundFlightNumber) ? ' field-triage-verify' : ''}`}
                title={
                  needsFlightVerification(formData.inboundFlightNumber)
                    ? 'Auto-populated after Triage; verify before saving'
                    : undefined
                }
                placeholder="Auto-populated"
              />
            </div>
          )}
          {!isScheduled && !isDeferred && (
            <div className="field-input-block">
              <span className="field-input-label">Chargeable Flight</span>
              <input
                type="text"
                value={formData.outboundFlightNumber}
                readOnly={isReadOnly}
                onChange={(e) => updateField('outboundFlightNumber', e.target.value.toUpperCase())}
                className={`field-input${needsFlightVerification(formData.outboundFlightNumber) ? ' field-triage-verify' : ''}`}
                title={
                  needsFlightVerification(formData.outboundFlightNumber)
                    ? 'Auto-populated after Triage; verify before saving'
                    : undefined
                }
                placeholder="Auto-populated"
              />
            </div>
          )}
        </div>
      </div>

      {/* General */}
      <div className="field-row">
        <label className="field-label">General</label>
        <div className="field-group">
          <div className="field-input-block">
            <span className="field-input-label">Status</span>
            {isReadOnly ? (
              <input
                type="text"
                value={formData.status.replace('_', ' ')}
                readOnly
                className="field-input"
              />
            ) : (
              <select
                value={formData.status}
                onChange={(e) => updateField('status', e.target.value)}
                disabled={awaitingTimeDeclared}
                className={`field-input${mode === 'create' && !formData.status && !awaitingTimeDeclared ? ' field-required-missing' : ''}${statusNeedsVerification ? ' field-triage-verify' : ''}`}
                title={
                  statusNeedsVerification
                    ? 'Auto-populated from Triage; verify before saving'
                    : undefined
                }
              >
                {mode === 'create' && <option value="">Select Status</option>}
                <option value="PENDING">Pending</option>
                <option value="IN_WORK">In Work</option>
                <option value="COMPLETED">Completed</option>
                {mode === 'edit' && <option value="DELETED">Deleted</option>}
                {canMove && <option value={MOVE_EVENT_OPTION}>Move Event</option>}
              </select>
            )}
          </div>
          {isMoveMode && (
            <div className="field-input-block">
              <span className="field-input-label">Move To</span>
              <select
                value={moveTargetType || ''}
                onChange={(e) => onMoveTargetChange && onMoveTargetChange(e.target.value)}
                className={`field-input${!moveTargetType ? ' field-required-missing' : ''}`}
              >
                <option value="">Select Record Type...</option>
                {moveTargetOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!!formData.moveStatus && !isMoveMode && (
            <div className="field-input-block">
              <span className="field-input-label">Move Status</span>
              <input
                type="text"
                value={`Moved to ${formData.moveStatus}`}
                readOnly
                className="field-input"
              />
            </div>
          )}
          <div className="field-input-block">
            <span className="field-input-label">Station</span>
            <input
              type="text"
              value={formData.station}
              readOnly={isReadOnly && !formData.diversion && !isScheduled}
              onChange={(e) => updateField('station', e.target.value)}
              className={`field-input${stationRequired && !(formData.station || '').trim() ? ' field-required-missing' : ''}${needsFlightVerification(formData.station) ? ' field-triage-verify' : ''}`}
              title={
                needsFlightVerification(formData.station)
                  ? 'Auto-populated after Triage; verify before saving'
                  : undefined
              }
              placeholder={isScheduled ? 'Enter station' : 'Auto-populated'}
            />
          </div>
          {!isDeferred && (
            <div className="field-input-block">
              <span className="field-input-label">Delay (Minutes)</span>
              <input
                type="text"
                inputMode="numeric"
                value={delayFieldValue}
                readOnly={isReadOnly}
                onFocus={() => setIsDelayFieldFocused(true)}
                onBlur={() => setIsDelayFieldFocused(false)}
                onChange={(e) => handleDelayChange(e.target.value)}
                className="field-input"
                placeholder="0"
              />
            </div>
          )}
          {!isDeferred && (
            <div className="field-input-block">
              <span className="field-input-label">DEA Impact (Pkgs)</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.deaImpactedPackageCount || ''}
                readOnly={isReadOnly}
                onChange={(e) => handleDeaChange(e.target.value)}
                className="field-input"
                placeholder="0"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AircraftGeneralSection;
